import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Center,
  Container,
  HStack,
  Spacer,
  Spinner,
  Stack,
  Tag,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { RequestStatus } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { addressSchema, formatAwb, getPrettyRequestStatus, modelUtils } from '@pelicargo/utils';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import z from 'zod';

import { BookingCancelledPage } from '../../components/BookingCancelledPage';
import { FPShipmentStatus } from '../../components/FPShipmentStatus';
import { LinkedRequestModal } from '../../components/LinkedRequestModal';
import { ModifyButton } from '../../components/ModifyButton';
import { NewLabeledValue } from '../../components/NewLabeledValue';
import { Page } from '../../components/Page';
import { ShareButton } from '../../components/ShareButton';
import { SupportButton } from '../../components/SupportButton';
import { shipmentStatusColorScheme, statusColorScheme } from '../../config/theme';
import { trpc } from '../../config/trpc';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { CONTAINER_2XL } from '../../utils/constants';
import { AFRFPDashboard } from './afrFPDashboard';
import { EXWFPDashboard } from './exwFPDashboard';
import { FCAFPDashboard } from './fcaFPDashboard';

type Params = {
  requestId?: string;
  quoteId?: string;
};

const schema = z.object({
  trucking_name: z.string().nullish(),
  pickup_ref: z.string().nullish(),
  trucking_ref: z.string().nullish(),
  estimated_pickup_at: z.coerce.date().nullish(),
  delivery_address: addressSchema.nullish(),
});

type FormSchema = z.infer<typeof schema>;

export const FPDashboardScreen = () => {
  const params: Params = useParams();
  const requestIdFromParam = Number(params?.requestId);
  const { targetAccount } = useTargetAccount();

  const [isBookingCancelled, setIsBookingCancelled] = useState(false);

  const {
    data: { quote } = {},
    isLoading,
    isRefetching,
    refetch,
    error,
  } = trpc.getFinalQuote.useQuery(
    { id: Number(params?.quoteId), ignoreMarkups: true },
    {
      enabled: !!params?.quoteId,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const bookedQuoteVariant = quote?.request?.booked_quote_variant;

  const methods = useForm<FormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(schema),
    defaultValues: {
      trucking_name: bookedQuoteVariant?.inland_service_quote_variant?.airline_ref,
      pickup_ref: bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '',
      trucking_ref: bookedQuoteVariant?.inland_service_quote_variant?.quote?.trucking_ref || '',
      estimated_pickup_at: bookedQuoteVariant?.inland_service_quote_variant?.quote?.estimated_pickup_at,
      delivery_address: bookedQuoteVariant?.inland_service_quote_variant?.quote?.delivery_address || undefined,
    },
  });

  useEffect(() => {
    const trucking_name = bookedQuoteVariant?.inland_service_quote_variant?.airline_ref;
    const pickup_ref = bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref || '';
    const trucking_ref = bookedQuoteVariant?.inland_service_quote_variant?.quote?.trucking_ref || '';
    const estimated_pickup_at = bookedQuoteVariant?.inland_service_quote_variant?.quote?.estimated_pickup_at;
    const delivery_address = bookedQuoteVariant?.inland_service_quote_variant?.quote?.delivery_address || undefined;

    if (trucking_name) methods.setValue('trucking_name', trucking_name);
    if (pickup_ref) methods.setValue('pickup_ref', pickup_ref);
    if (trucking_ref) methods.setValue('trucking_ref', trucking_ref);
    if (estimated_pickup_at) methods.setValue('estimated_pickup_at', estimated_pickup_at);
    if (delivery_address) methods.setValue('delivery_address', delivery_address);
  }, [
    methods,
    bookedQuoteVariant?.inland_service_quote_variant?.airline_ref,
    bookedQuoteVariant?.inland_service_quote_variant?.quote?.pickup_ref,
    bookedQuoteVariant?.inland_service_quote_variant?.quote?.estimated_pickup_at,
    bookedQuoteVariant?.inland_service_quote_variant?.quote?.delivery_address,
    bookedQuoteVariant?.inland_service_quote_variant?.quote?.trucking_ref,
  ]);

  const inlandQuote = bookedQuoteVariant?.inland_service_quote_variant?.quote;
  const isFCA = modelUtils.quote.isFca(inlandQuote);
  const isEXW = modelUtils.quote.isExw(inlandQuote);
  const isAFR = modelUtils.quote.isAfr(quote) && !isFCA && !isEXW;

  useEffect(() => {
    if (
      bookedQuoteVariant === null ||
      // Prevents flickering
      (!isNaN(bookedQuoteVariant?.quote_id) &&
        // Actual check to see if booking has been cancelled
        bookedQuoteVariant?.quote_id !== Number(params?.quoteId))
    ) {
      setIsBookingCancelled(true);
    }

    return () => {
      // Reset the state in the case that they cancel
      // and then book another one again, we want to make sure
      // the existing state doesn't persist
      setIsBookingCancelled(false);
    };
  }, [bookedQuoteVariant, quote?.request?.booked_quote_variant, quote?.request?.status, params?.quoteId]);

  useEffect(() => {
    refetch();
  }, [refetch, targetAccount?.id]);

  const alertMessage = useMemo(() => {
    if (quote?.request?.status === RequestStatus.BookingRequested) {
      return `Your booking is queued with ${quote?.provider?.name}.  You will receive an email once the flights are fully confirmed.`;
    }
    if (quote?.request?.status === RequestStatus.BookingChange) {
      return `We've received your request to edit the tender date and will let you know when the airline has confirmed the new booking.`;
    }
  }, [quote?.provider?.name, quote?.request?.status]);

  const cleanedOriginalAWB = quote?.original_customer_awb_number?.replace(/[^0-9]/g, '');
  const cleanedAWB = quote?.awb_number?.replace(/[^0-9]/g, '');
  const doBothAWBExist = !!cleanedOriginalAWB && !!cleanedAWB;
  const isAwbUpdated = cleanedOriginalAWB !== cleanedAWB && doBothAWBExist;
  const awbLabel = useMemo(() => {
    if (isAwbUpdated) {
      return (
        <Tag colorScheme="blue" size="xs" px="1" textTransform="capitalize" w="fit-content" fontSize="xs">
          Updated MAWB #
        </Tag>
      );
    }
    return 'AWB#';
  }, [isAwbUpdated]);

  if (isLoading || isRefetching) {
    return (
      <Page title="Confirmation" flex="1" py="24">
        <Center>
          <Spinner />
        </Center>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Confirmation" flex="1" py="24" w="full">
        <Center>
          <Alert status="error" variant="subtle" maxW="sm" borderRadius="lg">
            <AlertIcon />
            <Box>
              <AlertTitle>Error</AlertTitle>
              <AlertDescription title={error?.message}>{error?.message}</AlertDescription>
            </Box>
          </Alert>
        </Center>
      </Page>
    );
  }

  if (isBookingCancelled) {
    return <BookingCancelledPage requestId={String(requestIdFromParam)} />;
  }

  const airlineRef = bookedQuoteVariant?.airline_ref;

  return (
    <Page title="Booking Confirmation" flex="1">
      <Container w="full" py={{ base: '8', lg: '10' }} px={{ base: '4', lg: '12' }} maxW={CONTAINER_2XL}>
        <Stack direction={{ base: 'column', sm: 'row' }} justify="flex-end" w="full">
          <HStack>
            <Tag w="fit-content" colorScheme={statusColorScheme[quote?.request?.status]} size="lg" height="full">
              <Text fontWeight="bold">Request:</Text>&nbsp;{getPrettyRequestStatus(quote?.request?.status, true)}
            </Tag>
            <Tag
              w="fit-content"
              colorScheme={shipmentStatusColorScheme[quote?.request?.shipment_status]}
              size="lg"
              height="full"
            >
              <Text fontWeight="bold">Shipment:</Text>&nbsp;
              <FPShipmentStatus
                callback={refetch}
                isLoading={isRefetching}
                currentStatus={quote?.request?.shipment_status}
                requestId={quote?.request_id}
                variant="unstyled"
                // Without this, it is infuriatingly ONE pixel off.
                mt="1px"
              />
            </Tag>
          </HStack>
          <Spacer />
          <SupportButton requestId={requestIdFromParam} type="Booked" />
          <ShareButton requestId={requestIdFromParam} type="Booked" />
          <ModifyButton requestId={requestIdFromParam} refetchQuote={refetch} />
        </Stack>
        <Show
          if={
            quote?.request?.status === RequestStatus.BookingRequested ||
            quote?.request?.status === RequestStatus.BookingChange
          }
        >
          <Alert status="info" variant="top-accent" justifyContent="center" my="4">
            <AlertIcon />

            {alertMessage}
          </Alert>
        </Show>
        <Stack direction="column" w="full" spacing="10">
          <Stack direction="row" w="full" spacing={{ base: 5, sm: 10 }} mt="4" wrap="wrap">
            <NewLabeledValue label="Request#" value={`R${requestIdFromParam}`} />
            <Show if={!!quote?.awb_number}>
              <NewLabeledValue label={awbLabel} value={formatAwb(quote?.awb_number, '-')} />
            </Show>
            <Show if={!!airlineRef}>
              <NewLabeledValue label="Airline Reference#" value={airlineRef} />
            </Show>
            <Show if={!!quote?.request?.customer_ref}>
              <NewLabeledValue label="Your Reference#" value={quote?.request?.customer_ref} />
            </Show>
          </Stack>
          <Show if={isEXW}>
            <EXWFPDashboard quote={quote} />
          </Show>
          <Show if={isFCA}>
            <FCAFPDashboard quote={quote} />
          </Show>
          <Show if={isAFR}>
            <AFRFPDashboard quote={quote} />
          </Show>
        </Stack>
      </Container>
      <LinkedRequestModal request={quote?.request} />
    </Page>
  );
};
